"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.WeightCoverageResponse = exports.WeightCoverageRow = exports.WeightStandardsResponse = exports.WeightStandardDeltaItem = exports.WeightStandardRecord = exports.WeightRiskResponse = exports.WeightRiskDeltaItem = exports.WeightRiskRecord = exports.WeightPoliciesResponse = exports.WeightPolicyDeltaItem = exports.WeightPolicyRecord = exports.CatalogWeightCheckMode = exports.WeightPolicyMode = exports.WeightClustersResponse = exports.WeightClusterDeltaItem = exports.WeightClusterRecord = exports.WeightClusterSource = exports.ReplicatedClusterStatus = exports.WeightClusterStatus = exports.WEIGHT_SAMPLE_DEFAULTS = exports.WeightSamplesUploadRequest = exports.WeightSampleRecord = exports.WeightSampleRecordV2 = exports.WeightSampleCheckContext = exports.WeightSampleOriginScope = exports.WeightSampleRecordV1 = exports.CashierKeyProvenance = exports.SoftFlag = exports.EmbeddedKind = exports.WeighingLaneState = exports.ItemState = exports.WeighingOutcome = exports.WeighingMode = exports.WeightSampleSource = void 0;
exports.parseDeltaItems = parseDeltaItems;
exports.parseDeltaPage = parseDeltaPage;
exports.normaliseWeightSample = normaliseWeightSample;
exports.parseWeightSample = parseWeightSample;
const zod_1 = require("zod");
const sync_js_1 = require("./sync.js");
/**
 * Weight Management contracts (Sprint 8 — TRS §10), and the SecureScale
 * weight payloads.
 *
 * Self-checkout terminals capture per-item weight samples during
 * normal-mode and unknown-item flows. BOS aggregates samples
 * nightly into `weight_standards` (mean + tolerance band) that
 * terminals download for strict-mode verification.
 *
 * Sprint 8 BOS scope:
 *  - WeightSample upload envelope (terminals upload via
 *    /sync/weight-samples — flips 501 stub to ready).
 *  - WeightStandard download record (terminals download via
 *    /sync/weight-standards — flips deferred to ready).
 *  - Nightly aggregation job (BullMQ) computes standards.
 *  - Admin coverage dashboard + per-item drill.
 *
 * The wire shapes below (upload record v1 + v2, and the download records)
 * are hand-ported from SecureScale, which owns them:
 *   securitydb/contracts/schemas/weight-sample.ts
 *   securitydb/contracts/schemas/weight-download.ts
 *   securitydb/contracts/schemas/delta.ts, common.ts (inlined here)
 * Check-context port: securitydb fa9fb4d (SSC-76); earlier fields retain their existing pins.
 *
 * Port changes by the rules in securitydb/contracts/README.md ("Porting and
 * pinning"). `tests/weight.test.ts` checks this port against the pinned
 * JSON Schemas in `shared-scenarios/securescale-weight/json-schema/`. To
 * re-pin, copy the new `weight-sample.*` and `weight-download.*` files from
 * securitydb `contracts/dist/json-schema/` and update the commit above.
 *
 * Differences from the source, all without effect on the wire:
 *  - `SyncChangeKind` and `SyncCursor` come from `sync.ts` (identical
 *    definitions). The common building blocks (`SignedMg`, `ItemKey`, …)
 *    and the `deltaItem` / `deltaPage` builders are module-local, so the
 *    package barrel gains no generic names.
 *  - The coverage dashboard shapes at the end are platform-only.
 */
// ---------- Building blocks (securitydb common.ts) ----------
/** A mass in integer milligrams; may be negative (a removal). */
const SignedMg = zod_1.z.number().int();
/** A mass in integer milligrams, zero or more. */
const NonNegativeMg = zod_1.z.number().int().nonnegative();
/** A mass in integer milligrams, greater than zero. */
const PositiveMg = zod_1.z.number().int().positive();
/**
 * `item_key`: the item-master id of the sellable unit, or
 * `gtin:<14 digits>` when the barcode is not in the master.
 */
const ItemKey = zod_1.z.string().min(1).max(64);
/** Tenant-wide monotonic revision of a replicated row (the sync cursor). */
const Revision = zod_1.z.number().int().nonnegative();
const IsoDateTime = zod_1.z.string().datetime();
/** Milligrams per gram, for the v1 `weightGrams` conversion. */
const MG_PER_GRAM = 1000;
// ---------- Delta items (securitydb delta.ts) ----------
/**
 * The standard cursor-paged delta, as in `sync.ts`. Each item is
 * `create`, `update` or `delete` (a tombstone with the row id only). The
 * strict item schema is for writers; a reader skips an item whose `kind`
 * it does not know (`parseDeltaItems`), so a kind can be added without
 * breaking older readers.
 */
function deltaItem(payloadSchema) {
    return zod_1.z.discriminatedUnion('kind', [
        zod_1.z.object({ kind: zod_1.z.literal('create'), id: zod_1.z.string(), payload: payloadSchema }),
        zod_1.z.object({ kind: zod_1.z.literal('update'), id: zod_1.z.string(), payload: payloadSchema }),
        zod_1.z.object({ kind: zod_1.z.literal('delete'), id: zod_1.z.string() }),
    ]);
}
/** One page of a download domain. */
function deltaPage(itemSchema) {
    return zod_1.z.object({
        items: zod_1.z.array(itemSchema),
        /** The cursor to pass back on the next call. */
        cursor: sync_js_1.SyncCursor,
        /** More pages follow; call again with `cursor`. */
        hasMore: zod_1.z.boolean(),
        /** Records available from the request's cursor, on request only. */
        totalItems: zod_1.z.number().int().nonnegative().optional(),
        /** The server's snapshot point, for staleness display only. */
        snapshotAt: zod_1.z.string().datetime(),
    });
}
/**
 * Parse a page's raw `items` for a reader. Items with an unknown `kind`
 * are skipped. An item with a known kind that does not match its schema
 * is an error, as is anything that is not an object with a string `kind`.
 */
function parseDeltaItems(itemSchema, rawItems) {
    const items = [];
    let skipped = 0;
    rawItems.forEach((raw, index) => {
        const kind = zod_1.z.object({ kind: zod_1.z.string() }).safeParse(raw);
        if (!kind.success) {
            throw new Error(`delta item ${index}: missing or non-string kind`);
        }
        if (!sync_js_1.SyncChangeKind.safeParse(kind.data.kind).success) {
            skipped++;
            return;
        }
        items.push(itemSchema.parse(raw));
    });
    return { items, skipped };
}
/**
 * Parse a whole page for a reader: the envelope strictly, the items with
 * `parseDeltaItems`, so an unknown kind does not fail the page.
 */
function parseDeltaPage(itemSchema, raw) {
    const page = deltaPage(zod_1.z.unknown()).parse(raw);
    const { items, skipped } = parseDeltaItems(itemSchema, page.items);
    return { ...page, items, skipped };
}
// ---------- Upload: enums (securitydb weight-sample.ts) ----------
/** v1 provenance tag, unchanged. */
exports.WeightSampleSource = zod_1.z.enum(['sco_unknown_item', 'sco_normal_mode', 'pos_capture']);
/** `weight_observation.mode`. */
exports.WeighingMode = zod_1.z.enum([
    'normal',
    'learning_mode',
    'attendant_confirm',
    'attendant_override',
    'legacy_import',
    'test',
]);
/** `weight_observation.outcome`. */
exports.WeighingOutcome = zod_1.z.enum([
    'matched',
    'no_expectation',
    'mismatch_confirmed',
    'mismatch_ignored',
    'mismatch_removed',
    'mismatch_observed',
]);
/** The item's state at the time of the check. */
exports.ItemState = zod_1.z.enum([
    'excluded',
    'locked',
    'presence',
    'established',
    'learning',
    'seeded',
    'unknown',
]);
/**
 * `weight_observation.lane_state`. A lane in `none` records no weighings
 * and a lane in `fault` has paused the sale, so only these two appear on
 * a weighing.
 */
exports.WeighingLaneState = zod_1.z.enum(['healthy', 'degraded']);
/**
 * `weight_observation.embedded`: the weighing was checked against a
 * weight-embedded (`weight`) or price-embedded (`price`) label's declared
 * value. Such a weighing is **never learned**: the value belongs to one
 * labelled pack, not to the item.
 */
exports.EmbeddedKind = zod_1.z.enum(['weight', 'price']);
/**
 * `weight_observation.soft_flag`: something the lane noticed about the
 * weighing that did not stop the sale but should be visible centrally.
 *
 * - `price_label_mismatch`: a price-embedded label's weight (price ÷ unit
 *   price) did not match the scale, and the tenant's
 *   `sco.securityWeight.priceLabelMismatch` parameter is `soft`, so the
 *   lane accepted it instead of calling an attendant.
 *
 * Additive: a new flag is a new enum value, and SecureScale deploys it
 * before any lane sends it.
 */
exports.SoftFlag = zod_1.z.enum(['price_label_mismatch']);
/** Explicit observation of whether the cashier key was applied. */
exports.CashierKeyProvenance = zod_1.z.enum(['clear', 'applied', 'unknown']);
// ---------- Upload: v1 (unchanged) ----------
/**
 * One terminal-recorded weight observation, v1 — the record lanes send
 * today. `terminalId` is never in the body: BOS takes it from the
 * terminal's authentication.
 */
exports.WeightSampleRecordV1 = zod_1.z.object({
    clientUuid: zod_1.z.string().min(1).max(64),
    itemId: zod_1.z.string(),
    /** Grams (integer). SCO scales report 1g precision; we store as int. */
    weightGrams: zod_1.z.number().int().positive(),
    capturedAt: zod_1.z.string().datetime(),
    /** Provenance: 'sco_unknown_item' | 'sco_normal_mode' | 'pos_capture'. */
    source: exports.WeightSampleSource,
    /** Cashier / attendant who validated the sample (when applicable). */
    attestedByCashierId: zod_1.z.string().nullable(),
});
// ---------- Upload: v2 ----------
/** Immutable basket scope from the installed complete replica; never inferred on retry. */
exports.WeightSampleOriginScope = zod_1.z.object({
    tenantId: zod_1.z.string().uuid(),
    storeId: zod_1.z.string().min(1).max(128).regex(/\S/, 'Store must be nonblank'),
    versionId: zod_1.z.string().min(1).max(128).regex(/\S/, 'Version must be nonblank'),
});
/** Fields remain optional on historical routes; the origin-safe route requires them. */
exports.WeightSampleCheckContext = zod_1.z
    .object({
    version: zod_1.z.literal(1),
    quantity: zod_1.z.number().int().safe().min(1),
    deltaMg: zod_1.z.number().int().safe(),
    checkType: zod_1.z.enum(['interval', 'presence', 'plausibility', 'none']),
    action: zod_1.z.enum(['accept', 'attendant', 'noCheck']),
    reason: zod_1.z.string().min(1).max(64),
    floorMg: zod_1.z.number().int().safe().nonnegative(),
    expectation: zod_1.z
        .object({
        intervals: zod_1.z
            .array(zod_1.z
            .object({
            lowerMg: zod_1.z.number().int().safe(),
            upperMg: zod_1.z.number().int().safe(),
            source: zod_1.z.enum(['cluster', 'seed', 'locked', 'declared']),
            clusterId: zod_1.z.string().min(1).max(64).nullable(),
        })
            .strict()
            .refine((i) => i.lowerMg <= i.upperMg, 'Original interval bounds are inverted'))
            .min(1),
    })
        .strict()
        .nullable(),
    presenceMinimumMg: zod_1.z.number().int().safe().positive().optional(),
    plausibilityMaximumMg: zod_1.z.number().int().safe().positive().optional(),
    embedded: zod_1.z
        .object({
        kind: exports.EmbeddedKind,
        declaredMg: zod_1.z.number().int().safe().nonnegative().nullable(),
        roundingMg: zod_1.z.number().int().safe().nonnegative().nullable(),
    })
        .strict()
        .optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    if ((v.checkType === 'interval') !== (v.expectation !== null))
        ctx.addIssue({
            code: 'custom',
            path: ['expectation'],
            message: 'Original interval expectation must match check type',
        });
    if ((v.checkType === 'presence' || v.checkType === 'plausibility') &&
        v.presenceMinimumMg === undefined)
        ctx.addIssue({
            code: 'custom',
            path: ['presenceMinimumMg'],
            message: 'Original threshold is required',
        });
    if (v.checkType === 'plausibility' && v.plausibilityMaximumMg === undefined)
        ctx.addIssue({
            code: 'custom',
            path: ['plausibilityMaximumMg'],
            message: 'Original plausibility ceiling is required',
        });
});
const v2Fields = {
    originScope: exports.WeightSampleOriginScope.optional(),
    checkContext: exports.WeightSampleCheckContext.optional(),
    /** Units placed in this weighing (a multipack without its own key); 1 or more. */
    quantity: zod_1.z.number().int().min(1).optional(),
    /** The scale reported a stable reading. */
    stable: zod_1.z.boolean().optional(),
    mode: exports.WeighingMode.optional(),
    outcome: exports.WeighingOutcome.optional(),
    itemState: exports.ItemState.optional(),
    laneState: exports.WeighingLaneState.optional(),
    /** Scale serial number. */
    scaleId: zod_1.z.string().min(1).max(64).optional(),
    /** The scale's declared division at the time. */
    divisionMg: PositiveMg.optional(),
    /** The cashier key was applied. */
    cashierKey: zod_1.z.boolean().optional(),
    /** Absence is unknown; a false boolean alone cannot prove clear provenance. */
    cashierKeyProvenance: exports.CashierKeyProvenance.optional(),
    /** The replica revision the lane checked against. */
    replicaRevision: Revision.optional(),
    /** What was actually scanned; `itemId` carries the resolved item key. */
    barcode: zod_1.z.string().min(1).max(64).optional(),
    /**
     * Stable weight change attributed to the item(s). Takes precedence
     * over `weightGrams`, which stays for v1 readers.
     */
    deltaMg: SignedMg.optional(),
    /** Who acted, if anyone. Pseudonymous. */
    attendantId: zod_1.z.string().min(1).max(64).nullable().optional(),
    /**
     * Set when the weighing was checked against an embedded label (weight
     * or price). Such a weighing is never learned. Absent otherwise.
     */
    embedded: exports.EmbeddedKind.optional(),
    /**
     * Set when the lane accepted the weighing but flagged it for central
     * reporting (for example a price-label mismatch). Absent otherwise.
     */
    softFlag: exports.SoftFlag.optional(),
    /** Completed audit, not selection; absent means unknown. */
    audited: zod_1.z.boolean().optional(),
};
/**
 * v2. `itemId` keeps its v1 type and carries the resolved `item_key`;
 * `barcode` carries what was scanned.
 *
 * `weightGrams` is optional when `deltaMg` is present, so a weighing with
 * a delta of zero or less (which `weightGrams`, being positive, cannot
 * carry) can be expressed. A record with neither is invalid. When
 * `weightGrams` is sent it is still a positive integer.
 */
exports.WeightSampleRecordV2 = exports.WeightSampleRecordV1.extend({
    schemaVersion: zod_1.z.literal(2),
    weightGrams: zod_1.z
        .number()
        .int()
        .positive()
        .optional()
        .describe('Grams (integer). Optional in v2 when deltaMg is present; one of the two is required.'),
    ...v2Fields,
}).superRefine((r, ctx) => {
    if (r.checkContext &&
        (r.checkContext.quantity !== (r.quantity ?? 1) ||
            r.checkContext.deltaMg !== (r.deltaMg ?? (r.weightGrams ?? 0) * MG_PER_GRAM)))
        ctx.addIssue({
            code: 'custom',
            path: ['checkContext'],
            message: 'Original check must match weighing quantity and delta',
        });
    if (r.audited && (!r.attendantId || !r.attendantId.trim())) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['attendantId'],
            message: 'Completed audit requires attendant identity',
        });
    }
    if (r.weightGrams === undefined && r.deltaMg === undefined) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['weightGrams'],
            message: 'a v2 record needs weightGrams or deltaMg',
        });
    }
});
// ---------- Upload: any supported version ----------
/**
 * What `/sync/weight-samples` accepts. A record without `schemaVersion`
 * (or with `schemaVersion: 1`) is v1. A record that claims v2 must be a
 * valid v2 record; it never falls back to v1. An unknown version is
 * rejected. Unknown extra fields are tolerated and dropped (zod's
 * default), so a record from a newer lane still parses.
 *
 * Forwarding to SecureScale must relay the request body as sent, not a
 * value parsed with this schema: parsing drops fields a later version
 * adds.
 */
exports.WeightSampleRecord = zod_1.z.union([
    exports.WeightSampleRecordV2,
    exports.WeightSampleRecordV1.extend({ schemaVersion: zod_1.z.literal(1).optional() }),
]);
exports.WeightSamplesUploadRequest = zod_1.z.object({
    records: zod_1.z.array(exports.WeightSampleRecord).min(1).max(5000),
});
// ---------- Upload: normalisation ----------
/** Defaults for fields a v1 record (or a sparse v2 record) leaves out. */
exports.WEIGHT_SAMPLE_DEFAULTS = {
    mode: 'normal',
    outcome: 'matched',
    quantity: 1,
};
/** Normalise a parsed record of any supported version. */
function normaliseWeightSample(record) {
    const base = {
        clientUuid: record.clientUuid,
        itemKey: record.itemId,
        occurredAt: record.capturedAt,
        source: record.source,
        attestedByCashierId: record.attestedByCashierId,
    };
    if (record.schemaVersion !== 2) {
        return {
            schemaVersion: 1,
            ...base,
            deltaMg: record.weightGrams * MG_PER_GRAM,
            ...exports.WEIGHT_SAMPLE_DEFAULTS,
            stable: null,
            itemState: null,
            laneState: null,
            scaleId: null,
            divisionMg: null,
            cashierKey: null,
            cashierKeyProvenance: 'unknown',
            replicaRevision: null,
            barcode: null,
            attendantId: null,
            embedded: null,
            softFlag: null,
        };
    }
    return {
        schemaVersion: 2,
        ...base,
        deltaMg: v2DeltaMg(record),
        quantity: record.quantity ?? exports.WEIGHT_SAMPLE_DEFAULTS.quantity,
        mode: record.mode ?? exports.WEIGHT_SAMPLE_DEFAULTS.mode,
        outcome: record.outcome ?? exports.WEIGHT_SAMPLE_DEFAULTS.outcome,
        stable: record.stable ?? null,
        itemState: record.itemState ?? null,
        laneState: record.laneState ?? null,
        scaleId: record.scaleId ?? null,
        divisionMg: record.divisionMg ?? null,
        cashierKey: record.cashierKey ?? null,
        cashierKeyProvenance: record.cashierKeyProvenance ?? 'unknown',
        replicaRevision: record.replicaRevision ?? null,
        barcode: record.barcode ?? null,
        attendantId: record.attendantId ?? null,
        embedded: record.embedded ?? null,
        softFlag: record.softFlag ?? null,
        ...(record.audited === undefined ? {} : { audited: record.audited }),
        ...(record.originScope === undefined ? {} : { originScope: record.originScope }),
        ...(record.checkContext === undefined ? {} : { checkContext: record.checkContext }),
    };
}
/** `deltaMg` if present, else `weightGrams × 1000`; one of them is required. */
function v2DeltaMg(record) {
    if (record.deltaMg !== undefined)
        return record.deltaMg;
    if (record.weightGrams !== undefined)
        return record.weightGrams * MG_PER_GRAM;
    throw new Error('a v2 record needs weightGrams or deltaMg');
}
/** Parse untrusted input of any supported version and normalise it. */
function parseWeightSample(input) {
    return normaliseWeightSample(exports.WeightSampleRecord.parse(input));
}
// ---------- Download: sync.weight-clusters (securitydb weight-download.ts) ----------
/*
 * What SecureScale serves, through the BOS forwarder, to the lane replica.
 *
 * | Domain                  | Endpoint                     | Record               |
 * | ----------------------- | ---------------------------- | -------------------- |
 * | `sync.weight-clusters`  | `GET /sync/weight-clusters`  | WeightClusterRecord  |
 * | `sync.weight-policies`  | `GET /sync/weight-policies`  | WeightPolicyRecord   |
 * | `sync.weight-risk`      | `GET /sync/weight-risk`      | WeightRiskRecord     |
 * | `sync.weight-standards` | `GET /sync/weight-standards` | WeightStandardRecord |
 *
 * `tenant_id` is never on the wire; it comes from the authentication.
 * Unknown extra fields are tolerated and dropped.
 */
/** `weight_cluster.status`. */
exports.WeightClusterStatus = zod_1.z.enum(['candidate', 'established', 'retired']);
/** Statuses a replicated cluster can have. A retired cluster is a `delete`. */
exports.ReplicatedClusterStatus = zod_1.z.enum(['candidate', 'established']);
/** `weight_cluster.source`. */
exports.WeightClusterSource = zod_1.z.enum(['learned', 'legacy', 'seed']);
/** One non-retired cluster. The delta item id is `clusterId`. */
exports.WeightClusterRecord = zod_1.z
    .object({
    confidence: zod_1.z.enum(['provisional', 'trusted', 'narrow']).optional(),
    profileScope: zod_1.z.enum(['tenant', 'small_tenant']).optional(),
    independentStores: zod_1.z.number().int().nonnegative().optional(),
    independentAttendants: zod_1.z.number().int().nonnegative().optional(),
    independentSamples: zod_1.z.number().int().nonnegative().optional(),
    clusterId: zod_1.z.string().min(1).max(64),
    itemKey: ItemKey,
    status: exports.ReplicatedClusterStatus,
    /** Robust centre, per unit. */
    centerMg: PositiveMg,
    /** Raw robust spread `s = 1.4826 × MAD`, per unit. */
    spreadMg: NonNegativeMg,
    /** Pooled σ (the spread pooled with the prior), per unit. The lane's check uses it. */
    sigmaMg: NonNegativeMg,
    /** Interval floor `c_div × division_mg + noise_mg`. The lane's check uses it. */
    floorMg: NonNegativeMg,
    /** Canonical SSC-164 scale division; absence remains unknown. */
    divisionMg: PositiveMg.optional(),
    /** Per-unit acceptance interval. */
    lowerMg: NonNegativeMg,
    upperMg: PositiveMg,
    /** Support: weighings, distinct registers, distinct stores. */
    n: zod_1.z.number().int().nonnegative(),
    registers: zod_1.z.number().int().nonnegative(),
    stores: zod_1.z.number().int().nonnegative(),
    source: exports.WeightClusterSource,
    firstSeen: IsoDateTime,
    lastSeen: IsoDateTime,
    revision: Revision,
})
    .refine((c) => c.lowerMg <= c.centerMg && c.centerMg <= c.upperMg, {
    message: 'lowerMg <= centerMg <= upperMg',
    path: ['centerMg'],
});
exports.WeightClusterDeltaItem = deltaItem(exports.WeightClusterRecord);
exports.WeightClustersResponse = deltaPage(exports.WeightClusterDeltaItem);
// ---------- Download: sync.weight-policies ----------
/** `weight_item_policy.mode`. */
exports.WeightPolicyMode = zod_1.z.enum(['learn', 'locked', 'presence', 'excluded']);
/** Catalog `weightCheckMode` a policy was seeded from. */
exports.CatalogWeightCheckMode = zod_1.z.enum(['disabled', 'presence', 'tolerance']);
/**
 * One item policy: a chain row (`storeId` null) or a row for the lane's
 * store, which overrides it. The delta item id is `id`.
 */
exports.WeightPolicyRecord = zod_1.z
    .object({
    id: zod_1.z.string().min(1).max(64),
    storeId: zod_1.z.string().min(1).max(64).nullable(),
    itemKey: ItemKey,
    mode: exports.WeightPolicyMode,
    /** Bounds for `locked`, per unit. */
    lockedLowerMg: NonNegativeMg.nullable(),
    lockedUpperMg: PositiveMg.nullable(),
    /** Item-master weight, if any. */
    seedMg: PositiveMg.nullable(),
    seededFrom: exports.CatalogWeightCheckMode.nullable(),
    revision: Revision,
})
    .refine((p) => p.mode !== 'locked' ||
    (p.lockedLowerMg !== null && p.lockedUpperMg !== null && p.lockedLowerMg <= p.lockedUpperMg), { message: 'locked needs lockedLowerMg <= lockedUpperMg', path: ['lockedLowerMg'] });
exports.WeightPolicyDeltaItem = deltaItem(exports.WeightPolicyRecord);
exports.WeightPoliciesResponse = deltaPage(exports.WeightPolicyDeltaItem);
// ---------- Download: sync.weight-risk ----------
/** Substitution risk for one item in the lane's store. */
exports.WeightRiskRecord = zod_1.z.object({
    id: zod_1.z.string().min(1).max(64),
    storeId: zod_1.z.string().min(1).max(64),
    itemKey: ItemKey,
    /** max over Y of p(Y→X) × gain; zero or more. */
    riskScore: zod_1.z.number().nonnegative().finite(),
    /** The Y that achieves the maximum, if any. */
    topTwinKey: ItemKey.nullable(),
    revision: Revision,
});
exports.WeightRiskDeltaItem = deltaItem(exports.WeightRiskRecord);
exports.WeightRiskResponse = deltaPage(exports.WeightRiskDeltaItem);
// ---------- Download: sync.weight-standards (compatibility, unchanged) ----------
/**
 * Per-item standard. Terminals match scanned weight against
 * `expectedGrams ± toleranceGrams` in strict mode. SecureScale serves the
 * same record from its clusters during the rollout.
 */
exports.WeightStandardRecord = zod_1.z.object({
    id: zod_1.z.string(),
    itemId: zod_1.z.string(),
    expectedGrams: zod_1.z.number().int().positive(),
    toleranceGrams: zod_1.z.number().int().min(0),
    sampleCount: zod_1.z.number().int().min(0),
    /** When the standard was last recomputed by the nightly job. */
    updatedAt: zod_1.z.string().datetime(),
});
exports.WeightStandardDeltaItem = deltaItem(exports.WeightStandardRecord);
exports.WeightStandardsResponse = deltaPage(exports.WeightStandardDeltaItem);
// ---------- Admin REST surface (coverage dashboard) ----------
exports.WeightCoverageRow = zod_1.z.object({
    itemId: zod_1.z.string(),
    itemSku: zod_1.z.string(),
    itemName: zod_1.z.string(),
    /** Has a standard been computed? */
    standardEstablished: zod_1.z.boolean(),
    expectedGrams: zod_1.z.number().int().nullable(),
    toleranceGrams: zod_1.z.number().int().nullable(),
    sampleCount: zod_1.z.number().int().min(0),
    /** When the most recent sample was captured (null if none). */
    lastSampleAt: zod_1.z.string().datetime().nullable(),
});
exports.WeightCoverageResponse = zod_1.z.object({
    rows: zod_1.z.array(exports.WeightCoverageRow),
    rollup: zod_1.z.object({
        /** Items that are variable-weight (per the catalog flag). */
        variableWeightItems: zod_1.z.number().int().min(0),
        /** Of those, how many have an established standard. */
        standardsEstablished: zod_1.z.number().int().min(0),
        /** Coverage percentage (0-100). */
        coveragePercent: zod_1.z.number().min(0).max(100),
    }),
});
//# sourceMappingURL=weight.js.map